import { useController, type Control, type FieldPath, type FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface CheckboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CheckboxGroupProps<TFieldValues extends FieldValues = FieldValues> {
  /** The field name in the form */
  name: FieldPath<TFieldValues>;
  /** React Hook Form control object */
  control: Control<TFieldValues>;
  /** Array of checkbox options */
  options: CheckboxOption[];
  /** Validation rules for the field */
  rules?: Parameters<typeof useController<TFieldValues>>[0]["rules"];
  /** Whether the checkbox group is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Layout direction for the checkboxes */
  layout?: "vertical" | "horizontal";
}

export default function CheckboxGroup<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  options,
  rules,
  disabled,
  className = "",
  layout = "vertical",
}: CheckboxGroupProps<TFieldValues>) {
  const { t } = useTranslation();
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    rules,
  });

  const currentValue = (field.value as string[]) || [];
  
  const handleCheckboxChange = (value: string, checked: boolean) => {
    if (checked) {
      field.onChange([...currentValue, value]);
    } else {
      field.onChange(currentValue.filter((item) => item !== value));
    }
  };

  const containerClass = layout === "horizontal" 
    ? "d-flex gap-4 flex-wrap" 
    : "d-flex flex-column gap-2";

  return (
    <div className={className}>
      <div className={containerClass}>
        {options.map((option) => {
          const optionId = `${name}-${option.value}`;
          const isChecked = currentValue.includes(option.value);
          const isOptionDisabled = disabled || option.disabled;
          
          return (
            <div key={option.value} className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={optionId}
                checked={isChecked}
                onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
                onBlur={field.onBlur}
                disabled={isOptionDisabled}
              />
              <label
                className="form-check-label"
                htmlFor={optionId}
                style={{ cursor: isOptionDisabled ? "not-allowed" : "pointer" }}
              >
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
      {error && (
        <div className="invalid-feedback d-block">
          {error.message ? t(error.message) : ""}
        </div>
      )}
    </div>
  );
}

